import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { Term } from '@/types/terms'

/**
 * Word cloud shown in the detail pane while nothing is selected.
 *
 * Doubles as the concept-building visual: terms already in a concept wear
 * their concept's colour; while a build runs, the still-ungrouped terms drift
 * gently (unsettled), and each newly formed concept pops its members once and
 * settles them. The metaphor is deliberate — grouping a term visibly settles
 * it — and it keeps the cloud, the progress bar above it and the Mõisted tab
 * telling one story. (Connecting lines were tried and dropped: measuring a
 * reflowing flex layout never looked right.)
 */
const MAX_WORDS = 120
const MIN_PX = 12
const MAX_PX = 34

/**
 * Deterministic hash: scatter layout, float phase and concept hue all derive
 * from stable ids, so nothing jumps between renders.
 */
function hashId(n: number): number {
  let x = n ^ 0x9e3779b9
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b)
  x = Math.imul(x ^ (x >>> 13), 0x45d9f3b)
  return (x ^ (x >>> 16)) >>> 0
}

/** Language carries hue for ungrouped terms, frequency carries size. */
const LANG_CLASS: Record<string, string> = {
  et: 'text-foreground/80',
  en: 'text-primary/70',
  fi: 'text-success/70',
}

/** Stable, well-spread hue per concept. */
const hueOf = (conceptId: number) => hashId(conceptId) % 360

export function TermCloud({
  terms,
  onSelect,
  liveGroups = [],
  freshIds,
  building = false,
}: {
  terms: Term[]
  onSelect: (t: Term) => void
  /** Concepts formed during the current build run (ids arrive over SSE). */
  liveGroups?: { id: number; members: number[] }[]
  /** Members of the most recent concepts — these get the pop animation. */
  freshIds?: Set<number>
  building?: boolean
}) {
  const { t } = useTranslation()

  /** term_id -> concept id, persisted membership plus this run's arrivals. */
  const groupOf = useMemo(() => {
    const m = new Map<number, number>()
    for (const x of terms) if (x.concept_id != null) m.set(x.term_id, x.concept_id)
    for (const g of liveGroups) for (const id of g.members) m.set(id, g.id)
    return m
  }, [terms, liveGroups])

  /**
   * Words arranged into concept clusters.
   *
   * Members of one concept sit side by side in their own box, and the boxes
   * flow: adjacency reads as "these are one thing" far faster than matching
   * colours across a scattered cloud, and the ungrouped singletons left over
   * are exactly the work still to do.
   *
   * Nested flex-wrap rather than computed positions — the browser does the
   * layout, so nothing can overlap and it stays responsive. (Measuring a
   * reflowing layout to draw between words was tried and abandoned.)
   */
  const clusters = useMemo(() => {
    const top = [...terms].sort((a, b) => b.freq - a.freq).slice(0, MAX_WORDS)
    if (!top.length) return []
    const logs = top.map((x) => Math.log(x.freq + 1))
    const lo = Math.min(...logs)
    const span = Math.max(...logs) - lo || 1
    const sized = top.map((term, i) => ({
      term,
      px: MIN_PX + ((logs[i] - lo) / span) * (MAX_PX - MIN_PX),
    }))

    // Group by concept; every ungrouped term becomes its own singleton box so
    // the two kinds flow together instead of being separated into sections.
    const byConcept = new Map<string, { key: string; cid: number | null; words: typeof sized }>()
    for (const w of sized) {
      const cid = groupOf.get(w.term.term_id) ?? null
      const key = cid == null ? `t${w.term.term_id}` : `c${cid}`
      const box = byConcept.get(key) ?? { key, cid, words: [] }
      box.words.push(w)
      byConcept.set(key, box)
    }
    // Same deterministic scatter as before, now applied to the boxes: stable
    // across renders, and not a frequency-ranked list in disguise.
    return [...byConcept.values()].sort(
      (a, b) => hashId(a.cid ?? a.words[0].term.term_id) - hashId(b.cid ?? b.words[0].term.term_id),
    )
  }, [terms, groupOf])

  if (!clusters.length) return null

  return (
    <div className="scrollbar-thin flex h-full flex-col overflow-y-auto">
      <p className="shrink-0 px-4 pt-3 text-center text-[11px] text-muted-foreground">
        {t('terms.cloudHint')}
      </p>
      <div className="flex flex-1 items-center p-6">
        <div className="flex w-full flex-wrap items-baseline justify-center gap-x-4 gap-y-2">
          {clusters.map((box) => (
            <div
              key={box.key}
              className={cn(
                'flex flex-wrap items-baseline justify-center gap-x-2',
                // A concept gets a faint tinted frame so the cluster reads as
                // one object; singletons get nothing and just float.
                box.cid != null &&
                  box.words.length > 1 &&
                  'rounded-md px-2 py-0.5 ring-1 ring-inset',
              )}
              style={
                box.cid != null && box.words.length > 1
                  ? {
                      // Same hue as the words inside, barely there.
                      backgroundColor: `hsl(${hueOf(box.cid)} 60% 45% / 0.06)`,
                      // @ts-expect-error -- CSS custom property for ring colour
                      '--tw-ring-color': `hsl(${hueOf(box.cid)} 60% 45% / 0.22)`,
                    }
                  : undefined
              }
              title={
                box.cid != null && box.words.length > 1
                  ? `#${box.cid} · ${box.words.map((w) => w.term.value).join(' · ')}`
                  : undefined
              }
            >
          {box.words.map(({ term, px }) => {
            const cid = groupOf.get(term.term_id)
            const grouped = cid != null
            const fresh = !!freshIds?.has(term.term_id)
            return (
              <button
                key={term.term_id}
                type="button"
                onClick={() => onSelect(term)}
                title={`${term.value} · ${term.freq}×`}
                style={{
                  fontSize: `${px}px`,
                  // Grouped terms wear their concept's colour; ungrouped keep
                  // the language hue via class.
                  ...(grouped ? { color: `hsl(${hueOf(cid)} 60% 45%)` } : {}),
                  // Desynchronised drift, phase from the stable id.
                  ...(building && !grouped
                    ? {
                        animationDelay: `${hashId(term.term_id) % 3000}ms`,
                        animationDuration: `${4000 + (hashId(term.term_id) % 3000)}ms`,
                      }
                    : {}),
                }}
                className={cn(
                  'relative max-w-full truncate leading-snug transition-colors hover:text-primary',
                  !grouped && (LANG_CLASS[term.lang] ?? 'text-muted-foreground'),
                  // Ungrouped terms drift while the build runs — unsettled;
                  // grouping a term visibly settles it.
                  building && !grouped && 'term-float',
                  fresh && 'term-pop',
                )}
              >
                {term.value}
              </button>
            )
          })}
            </div>
          ))}
        </div>
      </div>
      {terms.length > MAX_WORDS && (
        <p className="shrink-0 pb-2 text-center text-[10px] text-muted-foreground/70">
          {t('actions.andMore', { count: terms.length - MAX_WORDS })}
        </p>
      )}
    </div>
  )
}
